import { F0Box, F0Heading, F0Select, F0Text } from '@factorialco/f0-react'
import Link from '@factorialco/f0-react/icons/app/Link'

import { TrainingsTrainingClassPaymentStatusEnum } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import { amountWithCurrency } from 'modules/finance/lib/budgets'

interface BudgetOption {
  value: string
  label: string
  icon?: typeof Link
}

interface CostsSummaryRowProps {
  totalCostCents: number
  currency: string
  budgetOptions: BudgetOption[]
  selectedBudgetId: string | null
  paymentStatus: TrainingsTrainingClassPaymentStatusEnum | null
  onBudgetChange: (budgetId: string | null) => void
  onPaymentStatusChange: (status: TrainingsTrainingClassPaymentStatusEnum) => void
  hasBudgetsFeature: boolean
  budgetDisabled?: boolean
}

export const CostsSummaryRow = ({
  totalCostCents,
  currency,
  budgetOptions,
  selectedBudgetId,
  paymentStatus,
  onBudgetChange,
  onPaymentStatusChange,
  hasBudgetsFeature,
  budgetDisabled,
}: CostsSummaryRowProps) => {
  const {
    access: { locale },
  } = useCurrentSession()

  return (
    <F0Box display="grid" columns="3" gap="md" width="full" alignItems="stretch" paddingBottom="xl">
      <F0Box
        display="flex"
        flexDirection="column"
        padding="md"
        gap="xs"
        border="default"
        borderRadius="md"
        borderColor="secondary"
        colSpan="1"
      >
        <F0Text variant="label" content={i18n.t('trainings.classes.costs.summary.total_cost')} />
        <F0Text
          variant="description"
          content={i18n.t('trainings.classes.costs.summary.total_cost_desc')}
        />
        <F0Heading
          variant="heading-large"
          content={amountWithCurrency(totalCostCents, currency, locale)}
        />
      </F0Box>

      {hasBudgetsFeature && (
        <F0Box
          display="grid"
          columns="2"
          padding="md"
          gap="md"
          border="default"
          borderRadius="md"
          borderColor="secondary"
          colSpan="2"
        >
          <F0Box width="full">
            <F0Select
              label={i18n.t('trainings.classes.costs.linked_budget')}
              placeholder={i18n.t('trainings.classes.costs.no_budget_linked')}
              showSearchBox
              searchBoxPlaceholder={i18n.t('common.search')}
              options={budgetOptions.map((option) => ({
                ...option,
                icon: Link,
              }))}
              value={selectedBudgetId ?? undefined}
              onChange={(value: string | null) => onBudgetChange(value ?? null)}
              size="md"
              clearable
              disabled={budgetDisabled}
              defaultItem={{
                value: '',
                label: i18n.t('trainings.classes.costs.no_budget_linked'),
                icon: Link,
              }}
            />
          </F0Box>

          <F0Box width="full">
            <F0Select<TrainingsTrainingClassPaymentStatusEnum>
              label={i18n.t('trainings.classes.payment_status.label')}
              options={[
                {
                  value: 'pending',
                  label: i18n.t('trainings.classes.payment_status.pending'),
                },
                {
                  value: 'paid',
                  label: i18n.t('trainings.classes.payment_status.paid'),
                },
              ]}
              value={paymentStatus ?? undefined}
              onChange={onPaymentStatusChange}
              size="md"
            />
          </F0Box>
        </F0Box>
      )}
    </F0Box>
  )
}
